import React from "react";
import { X, ImageOff, FileText, ExternalLink } from "lucide-react";
import VehicleInspectionDiagram from "../../components/VehicleInspectionDiagram";

/**
 * InspectionOverlayModal — staff-facing damage-claim view.
 *
 * Renders the customer's tap-diagram markers (read-only) side-by-side with
 * the manager's pickup walk-around photos and (if the rental has been
 * returned) the return photos. Purpose: when a customer disputes a damage
 * charge later, staff can pull this modal up and show both what the
 * customer said the vehicle looked like at pickup AND the manager's actual
 * photos — a defensible paper trail without ambiguity.
 *
 * Manager PDF Preview: The contract PDF QR now points to
 * ${PUBLIC_APP_URL}/b/{id}?token=…, which renders BookingShare.jsx —
 * scanning from a printed contract on a phone opens the full online
 * agreement + inspection video in one tap. No separate wiring needed here.
 */
export default function InspectionOverlayModal({ rental, onClose }) {
  const booking = rental || {};
  const customerInspection = booking.customer_inspection || { markers: [], acknowledged: false };
  const pickupPhotos = booking.pickup_photo_ids || [];
  const returnPhotos = booking.return_photo_ids || [];
  const agreementNo = booking.agreement_number || booking.id;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 overflow-y-auto"
         data-testid="inspection-overlay-modal">
      <div className="bg-ink-950 border border-white/10 rounded-3xl max-w-5xl w-full my-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-ink-950 rounded-t-3xl">
          <div>
            <div className="label-caps text-cyan text-[10px]">Damage Claim Evidence</div>
            <h2 className="font-serif text-xl text-white mt-0.5">
              {booking.vehicle_name || "Rental"} · <span className="text-white/50 font-mono text-sm">{agreementNo}</span>
            </h2>
            <div className="text-[11px] text-white/50 mt-0.5">
              Customer inspection vs. staff walk-around — side-by-side for dispute resolution.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`/api/admin/bookings/${booking.id}/contract-pdf`}
              target="_blank" rel="noopener noreferrer"
              data-testid="damage-open-pdf"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] label-caps border border-white/15 text-white/70 hover:text-white hover:border-cyan/40"
            >
              <FileText size={12} /> Open PDF <ExternalLink size={10} />
            </a>
            <button
              type="button"
              data-testid="inspection-overlay-close"
              onClick={onClose}
              className="w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Customer's inspection diagram (read-only) */}
          <section>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="label-caps text-white/50 text-[10px] mb-2">1 · Customer tap-diagram (what they said at booking)</div>
                <VehicleInspectionDiagram
                  value={customerInspection}
                  onChange={() => {}}
                  readOnly
                  testId="damage-customer-diagram"
                />
              </div>

              {/* Manager pickup photos */}
              <div>
                <div className="label-caps text-white/50 text-[10px] mb-2">
                  2 · Staff walk-around at pickup ({pickupPhotos.length} photo{pickupPhotos.length === 1 ? "" : "s"})
                </div>
                <PhotoWall
                  photoIds={pickupPhotos}
                  urlFor={(id) => `/api/public/pickup-photo/${id}`}
                  emptyLabel="No pickup photos on file"
                  testId="damage-pickup-photos"
                />
              </div>
            </div>
          </section>

          {/* Return photos — only when the rental has been returned */}
          {returnPhotos.length > 0 && (
            <section>
              <div className="label-caps text-white/50 text-[10px] mb-2">
                3 · Staff walk-around at return ({returnPhotos.length} photo{returnPhotos.length === 1 ? "" : "s"})
              </div>
              <PhotoWall
                photoIds={returnPhotos}
                urlFor={(id) => `/api/public/return-photo/${id}`}
                emptyLabel="No return photos on file"
                testId="damage-return-photos"
              />
            </section>
          )}

          {/* Metadata strip — IP, agreement #, timestamps */}
          <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <div className="label-caps text-white/50 text-[10px] mb-2">Audit trail</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
              <MetaCell k="Agreement #" v={agreementNo} />
              <MetaCell k="Customer IP" v={booking.customer_ip || "—"} />
              <MetaCell k="Signed at" v={fmt(booking.contract_signed_at)} />
              <MetaCell k="Policies at" v={fmt(booking.policies_accepted_at)} />
              <MetaCell k="Selfie at" v={fmt(booking.selfie_captured_at)} />
              <MetaCell k="DL front at" v={fmt(booking.dl_front_captured_at)} />
              <MetaCell k="DL back at" v={fmt(booking.dl_back_captured_at)} />
              <MetaCell k="Inspection at" v={fmt(customerInspection.acknowledged_at)} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function PhotoWall({ photoIds, urlFor, emptyLabel, testId }) {
  if (!photoIds || photoIds.length === 0) {
    return (
      <div data-testid={`${testId}-empty`} className="rounded-xl bg-white/[0.02] border border-dashed border-white/10 p-6 flex flex-col items-center justify-center text-white/40 gap-1.5" style={{ aspectRatio: "2 / 1" }}>
        <ImageOff size={22} />
        <span className="text-[11px]">{emptyLabel}</span>
      </div>
    );
  }
  return (
    <div data-testid={testId} className="grid grid-cols-3 gap-1.5">
      {photoIds.map((id) => (
        <a key={id} href={urlFor(id)} target="_blank" rel="noopener noreferrer"
           className="aspect-square rounded-lg overflow-hidden border border-white/10 bg-black hover:border-cyan/40 transition">
          <img src={urlFor(id)} alt="Walk-around" className="w-full h-full object-cover" />
        </a>
      ))}
    </div>
  );
}

function MetaCell({ k, v }) {
  return (
    <div>
      <div className="text-white/40 text-[9px] label-caps">{k}</div>
      <div className="text-white truncate font-mono text-[11px]">{v || "—"}</div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
